import React from "react";
import { Phone, X, Check } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useI18n } from "../../i18n/I18nProvider";
import { API } from "../../lib/api";

const TIMES = [
  { id: "asap", key: "asap" },
  { id: "morning", key: "morning" },
  { id: "afternoon", key: "afternoon" },
  { id: "evening", key: "evening" },
];

export default function CallbackWidget() {
  const { t, locale } = useI18n();
  const [open, setOpen] = React.useState(false);
  const [done, setDone] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [form, setForm] = React.useState({ full_name: "", phone: "", best_time: "asap", topic: "" });

  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/callback-request`, { ...form, locale });
      setDone(true);
      toast.success(t("callback.success"));
    } catch (err) {
      toast.error(t("common.error_submit"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <button
        data-testid="callback-fab"
        onClick={() => { setOpen(true); setDone(false); }}
        className="fixed z-40 bottom-24 right-5 sm:bottom-6 sm:right-6 inline-flex items-center justify-center size-14 bg-oj-ink text-white rounded-full shadow-lg hover:bg-zinc-800 transition-colors ring-1 ring-white/10"
        aria-label={t("nav.callback")}
        title={t("nav.callback")}
      >
        <Phone className="size-5" />
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:justify-end p-3 sm:p-6" data-testid="callback-modal">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setOpen(false)}
          />
          <div className="relative w-full sm:max-w-md rounded-2xl bg-white border border-zinc-200 shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-200">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{t("callback.eyebrow")}</div>
                <div className="font-display font-bold text-oj-ink mt-0.5">{t("callback.title")}</div>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="p-1.5 rounded-md hover:bg-zinc-100"
                data-testid="callback-close"
                aria-label="Close"
              >
                <X className="size-5" />
              </button>
            </div>

            {done ? (
              <div className="p-8 text-center" data-testid="callback-success">
                <div className="mx-auto size-12 rounded-full bg-oj-kiosk text-white grid place-items-center">
                  <Check className="size-6" />
                </div>
                <div className="mt-4 font-display text-xl font-bold text-oj-ink">{t("callback.success")}</div>
                <button
                  onClick={() => setOpen(false)}
                  className="mt-5 text-sm font-semibold text-oj-ink underline underline-offset-4"
                >
                  {t("common.close")}
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="p-5 space-y-3" data-testid="callback-form">
                <p className="text-sm text-zinc-600">{t("callback.sub")}</p>
                <input
                  data-testid="callback-input-name"
                  required
                  placeholder={t("callback.full_name")}
                  value={form.full_name}
                  onChange={set("full_name")}
                  className="w-full bg-zinc-50 border border-zinc-200 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:border-oj-ink"
                />
                <input
                  data-testid="callback-input-phone"
                  required
                  type="tel"
                  placeholder={t("callback.phone")}
                  value={form.phone}
                  onChange={set("phone")}
                  className="w-full bg-zinc-50 border border-zinc-200 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:border-oj-ink"
                />
                <input
                  data-testid="callback-input-topic"
                  placeholder={t("callback.topic")}
                  value={form.topic}
                  onChange={set("topic")}
                  className="w-full bg-zinc-50 border border-zinc-200 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:border-oj-ink"
                />
                <div>
                  <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-zinc-500 mb-2">
                    {t("callback.best_time")}
                  </div>
                  <div className="flex flex-wrap gap-1.5" data-testid="callback-times">
                    {TIMES.map((tm) => {
                      const active = form.best_time === tm.id;
                      return (
                        <button
                          type="button"
                          key={tm.id}
                          data-testid={`callback-time-${tm.id}`}
                          onClick={() => setForm((s) => ({ ...s, best_time: tm.id }))}
                          aria-pressed={active}
                          className={`text-xs font-semibold px-3 py-2 rounded-full border transition-all ${
                            active ? "bg-oj-ink text-white border-transparent" : "bg-white text-zinc-700 border-zinc-300 hover:border-zinc-900"
                          }`}
                        >
                          {t(`callback.${tm.key}`)}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="callback-submit"
                  className="w-full inline-flex items-center justify-center gap-1.5 bg-oj-ink text-white font-semibold px-4 py-3 rounded-md hover:bg-zinc-800 disabled:opacity-60"
                >
                  <Phone className="size-4" /> {submitting ? "..." : t("callback.submit")}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}
